import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Button, Loading } from '@/components/base';
import { useAuthContext } from '@/contexts/authContext';
import { UserRole } from '@/model/User.model';
import { schoolRoutes } from '@/routes/SchoolApp/router/routes';
import { toClassNames } from '@/utils';

interface MenuItem {
  getLabel: (t: TFunction<'schoolApp'>) => string;
  id: string;
  icon: string;
  selectedIcon: string;
  getRoute: () => string;
  roleAccess: UserRole[];
}
const menus: MenuItem[] = [
  {
    getLabel: (t) => t('schoolApp:sidebar.dashboard'),
    id: 'dashboard',
    icon: 'icon-[material-symbols--dashboard-outline-rounded]',
    selectedIcon: 'icon-[material-symbols--dashboard-rounded]',
    getRoute: () => schoolRoutes.SchoolGroupDashboard.getRoute(),
    roleAccess: [UserRole.SchoolGroupAdmin],
  },
  {
    getLabel: (t) => t('schoolApp:sidebar.schools'),
    id: 'schools',
    icon: 'icon-[material-symbols--school-outline-rounded]',
    selectedIcon: 'icon-[material-symbols--school-rounded]',
    getRoute: () => schoolRoutes.SchoolGroupSchoolList.getRoute(),
    roleAccess: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin],
  },
  {
    getLabel: (t) => t('schoolApp:sidebar.classrooms'),
    id: 'classrooms',
    icon: 'icon-[material-symbols--co-present-outline-rounded]',
    selectedIcon: 'icon-[material-symbols--co-present-rounded]',
    getRoute: () => schoolRoutes.SchoolGroupClassroomList.getRoute(),
    roleAccess: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin, UserRole.Instructor],
  },
  {
    getLabel: (t) => t('schoolApp:sidebar.users'),
    id: 'users',
    icon: 'icon-[material-symbols--group-outline-rounded]',
    selectedIcon: 'icon-[material-symbols--group]',
    getRoute: () => schoolRoutes.SchoolGroupUserList.getRoute(),
    roleAccess: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin, UserRole.Instructor],
  },
  //TODO: EDSY-1238 Low priority work on May Sprint
  // {
  //   getLabel: (t) => t('schoolApp:sidebar.favorites'),
  //   id: 'favorites',
  //   icon: 'icon-[material-symbols--bookmark-star-outline-rounded]',
  //   selectedIcon: 'icon-[material-symbols--bookmark-star-rounded]',
  //   getRoute: () =>
  //     schoolRoutes.SchoolLessonList.getRoute({
  //       schoolId: schoolId ?? user?.schoolId ?? '',,
  //       classroomId: classroomId ?? '',
  //     }),
  // },
];

export const SidebarMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, isAuthenticating } = useAuthContext();
  const { t } = useTranslation('schoolApp');

  const activePageState = useMemo(() => {
    const pageSegments = location.pathname.split('/').filter(Boolean).reverse();
    let activeMenu: MenuItem | undefined;

    for (const segment of pageSegments) {
      activeMenu = menus.find((menu) => menu.id === segment);
      if (activeMenu) {
        break;
      }
    }

    return activeMenu;
  }, [location.pathname]);

  if (!user || isAuthenticating) {
    return <Loading className="mt-8" />;
  }

  return (
    <div className="flex flex-col gap-4 bg-white px-4">
      {menus.map((menu) => {
        return (
          menu.roleAccess.includes(user.role) && (
            <Button
              key={menu.id}
              variant="unstyled"
              onClick={() => {
                navigate(menu.getRoute());
              }}
            >
              <div
                className={toClassNames(
                  'flex gap-2 rounded-md p-3 font-medium text-gray-700 hover:bg-blue-light active:bg-primary-50',
                  'transition duration-300 ease-in-out',
                  activePageState?.id === menu.id && 'bg-blue-light text-primary-500'
                )}
              >
                <span
                  className={toClassNames('text-2xl', activePageState?.id === menu.id ? menu.selectedIcon : menu.icon)}
                />
                {menu.getLabel(t)}
              </div>
            </Button>
          )
        );
      })}
    </div>
  );
};
